import type { FC } from "react";

import { Tag } from "@/components/ui/tag";

import type { ForeignNationalDependent } from "../../../../api/types/foreignNationalDependent";
import {
  ClientTable,
  type ClientTableColumn,
  type ClientTableEmpty
} from "../../components/table/ClientTable";
import { ClientTableRow } from "../../components/table/ClientTableRow";
import {
  BadgeCell,
  MutedCell,
  RowMenuCell,
  SkeletonRow,
  TitleCell,
  type BadgeVariant
} from "../../components/table/cells";
import { dependentName, relationshipLabel } from "../../lib/dependents";
import { ageFromBirthday, formatMdY, normalizeText } from "../../lib/format";
import {
  dependentFocusDomId,
  visaStatusTone,
  type VisaStatusTone
} from "./dependentsModel";

const COLUMNS: ClientTableColumn[] = [
  { id: "name", header: "Name", className: "min-w-55" },
  { id: "dob", header: "Date of Birth" },
  { id: "birth", header: "Country of Birth" },
  { id: "citizenship", header: "Citizenship" },
  { id: "status", header: "Visa Status" },
  { id: "valid", header: "Valid Until" },
  { id: "menu", header: "", className: "w-12" }
];

const TONE_VARIANT: Record<VisaStatusTone, BadgeVariant> = {
  none: "neutral",
  expired: "error",
  pending: "warning",
  active: "success"
};

export const validUntilLabel = (d: ForeignNationalDependent): string => {
  const status = normalizeText(d.current_immigration_status);
  if (d.immigration_status_expiration)
    return formatMdY(d.immigration_status_expiration);
  return status.toLowerCase().includes("pending") ? "Pending" : "N/A";
};

type Props = {
  dependents: ForeignNationalDependent[];
  isLoading: boolean;
  error?: string | null;
  onRetry?: () => void;
  empty: ClientTableEmpty;
  highlightedDomId: string | null;
  onEdit: (dependent: ForeignNationalDependent) => void;
  onRemove: (dependent: ForeignNationalDependent) => void;
};

/** Flat table of a client's dependents with an Edit / Remove row menu. */
export const DependentsTable: FC<Props> = ({
  dependents,
  isLoading,
  error,
  onRetry,
  empty,
  highlightedDomId,
  onEdit,
  onRemove
}) => (
  <ClientTable
    ariaLabel="Dependents"
    columns={COLUMNS}
    isLoading={isLoading}
    isEmpty={dependents.length === 0}
    error={error}
    onRetry={onRetry}
    empty={empty}
    renderSkeletonRow={(i) => (
      <SkeletonRow
        key={i}
        avatar
        widths={["w-40", "w-24", "w-24", "w-24", "w-20", "w-20", "w-6"]}
      />
    )}
  >
    {dependents.map((d) => {
      const name = dependentName(d) || "—";
      const age = ageFromBirthday(d.birthday);
      const status = normalizeText(d.current_immigration_status);
      const domId = dependentFocusDomId(d.dependent_id);
      return (
        <ClientTableRow
          key={d.dependent_id}
          domId={domId}
          highlighted={highlightedDomId === domId}
          onActivate={() => onEdit(d)}
          ariaLabel={name}
        >
          <TitleCell
            avatar={{ label: name }}
            title={name}
            trailing={<Tag>{relationshipLabel(d.relationship)}</Tag>}
          />
          <MutedCell>
            {d.birthday
              ? `${formatMdY(d.birthday)}${age === null ? "" : ` (${age})`}`
              : "—"}
          </MutedCell>
          <MutedCell>{normalizeText(d.country_of_birth) || "—"}</MutedCell>
          <MutedCell>
            {normalizeText(d.country_of_citizenship) || "—"}
          </MutedCell>
          <BadgeCell
            label={status || undefined}
            variant={TONE_VARIANT[visaStatusTone(d)]}
          />
          <MutedCell>{validUntilLabel(d)}</MutedCell>
          <RowMenuCell
            ariaLabel={`Actions for ${name}`}
            items={[
              {
                key: "edit",
                label: "Edit Details",
                icon: "FeatherEdit2",
                onSelect: () => onEdit(d)
              },
              {
                key: "remove",
                label: "Remove Dependent",
                icon: "FeatherTrash2",
                destructive: true,
                onSelect: () => onRemove(d)
              }
            ]}
          />
        </ClientTableRow>
      );
    })}
  </ClientTable>
);
